import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoItemResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useToast } from '@/components/ui/use-toast';

interface CreateItemInput {
  title: string;
  description?: string;
  sectionId?: string;
}

interface UpdateItemInput {
  title?: string;
  description?: string;
  status?: 'pending' | 'in_progress' | 'completed';
  sectionId?: string | null;
  sortOrder?: number;
}

interface UseTodoItemsReturn {
  createItem: (input: CreateItemInput) => Promise<TodoItemResponse>;
  updateItem: (vars: { itemId: string; input: UpdateItemInput }) => Promise<TodoItemResponse>;
  deleteItem: (itemId: string) => Promise<void>;
}

export function useTodoItems(listId: string): UseTodoItemsReturn {
  const queryClient = useQueryClient();
  const detailKey = queryKeys.todoLists.detail(listId);
  const { toast } = useToast();

  const onError = () => {
    toast({ title: 'Something went wrong', variant: 'destructive' });
  };

  const createMut = useMutation({
    mutationFn: (input: CreateItemInput) => api.createTodoItem(listId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onError,
  });

  const updateMut = useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: UpdateItemInput }) =>
      api.updateTodoItem(listId, itemId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onError,
  });

  const deleteMut = useMutation({
    mutationFn: (itemId: string) => api.deleteTodoItem(listId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onError,
  });

  return {
    createItem: (input) => createMut.mutateAsync(input),
    updateItem: (vars) => updateMut.mutateAsync(vars),
    deleteItem: (itemId) => deleteMut.mutateAsync(itemId),
  };
}
